import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { buildNav } from '@solum/experience'
import {
  visibleNav, reconcile, isNavDocument, NAV_EDIT_INSTRUCTIONS, useNavigationDisclosure,
  CustomizeRowMenu, NavigationDestination, NavigationSectionHead, NavigationSectionHeadRow,
  type NavDocument, type NavEditController, type NavEditTarget,
} from '@solum/ui'
import { Button, EntityIcon, Input } from '@solum/ui'
import {
  Activity, BadgeCheck, BellRing, BookUser, Boxes, Building2, Calculator, CalendarCheck, Camera,
  ChevronDown, ChevronRight, Clapperboard, ClipboardList, Clock, CreditCard, DoorOpen, FileCheck,
  FileText, Files, GraduationCap, GripVertical, Handshake, Inbox, KeyRound, Layers3, LifeBuoy,
  ListChecks, Mail, Megaphone, Package, PanelsTopLeft, PiggyBank, Pin, Plus, Repeat, RotateCcw,
  Scale, ScanSearch, Server, ShieldCheck, ShoppingCart, Store, Target, Truck, UserPlus, UserSearch,
  Users, Wallet, Workflow, type LucideIcon,
} from 'lucide-react'
import {
  useSchema, useSemantics, useEntityIdentities, useNavigation, useNavigationPin, usePinNavigation,
} from '../data/queries'
import { useRegistry } from '../data/registry'
import { useMyAreas } from '../access/access'
import { consoleProfile, showsFleetSurfaces } from '../data/profile'
import s from './Nav.module.css'
import { QueryBoundary, queryState } from '../data/QueryBoundary'
import { usePins } from './pins'
import type { PinnedItem } from './pins'

/**
 * What the router writes on the link of the page on screen (#2624).
 *
 * The rail's destination is the kit's `NavigationDestination` drawn on the
 * router's own `<Link>` (`asChild`). WHICH destination is current is the
 * router's decision — TanStack's, the one this rail always used (a section's
 * page stays current on its child routes) — and the router states it on the
 * element as `aria-current="page"`, which is the attribute the kit's paint
 * keys on. So the kit is told, never derives, and the painted and announced
 * states are one attribute. No class: the paint is the kit's.
 */
const ROUTER_STATES_CURRENT = { 'aria-current': 'page' } as const

/**
 * Runtime pin projection. Only destinations offered by the resolved navigation
 * are eligible: historical record/view pins are intentionally ignored rather
 * than replayed as a second workspace model.
 */
export function withPinnedNavigation(doc: NavDocument, pins: readonly PinnedItem[]): NavDocument {
  const offered = new Map(doc.groups.flatMap((group) => group.items.map((item) => [item.to, item] as const)))
  const items = pins.filter((pin) => pin.scope === 'navigation').flatMap((pin) => {
    const item = offered.get(pin.to)
    return item ? [{ ...item, label: pin.name || item.label }] : []
  })
  return items.length
    ? { ...doc, groups: [{ id: 'runtime-pinned', label: 'Pinned', items }, ...doc.groups] }
    : doc
}

// A module's display name — presentation only; the party model's core module
// leads as the "Directory".
/**
 * Display names for module groups. `buildNav` includes EVERY module that has
 * dictionaried entities and falls back to the raw module key, so a missing entry
 * here is a lowercase machine name in the sidebar, not an absent module.
 *
 * ── WHICH PATH STILL NEEDS THIS (#511) ─────────────────────────────────────
 * The BUILT-IN bar only. A configured document carries its own labels — that is
 * the point of it — so the moment a persona or a user has one, nothing here is
 * consulted. This map cannot be retired while the built-in bar exists, because
 * the built-in bar is generated from the installed metamodel and the metamodel
 * has module KEYS, not human names. It goes when the JSX-equivalent below does.
 *
 * `kyc` used to be labelled "Compliance" while the actual `compliance` module —
 * the ISMS: frameworks, risks, controls, treatments, DPIAs, incidents, suppliers,
 * audits, 89 views and 378 field_meta rows — had no entry at all. So the one nav
 * item saying "Compliance" led to KYC & seller identity, and the ISMS appeared,
 * if at all, as lowercase `compliance` (#331).
 *
 * Two different domains. KYC is verified recipients and seller identity for
 * e-invoicing; compliance is ISO 27001 / NIS2 / GDPR. They must not share a name.
 *
 * #331 fixed ONE instance. Measuring the whole set afterwards found THIRTEEN
 * modules with no entry — `marketing`, `engagements`, `partners` and every
 * `cocco-*` among them — so the sidebar of a Coccolare deployment read
 * `cocco-loyalty`, `cocco-fefo`, `cocco-quiz`. A one-off fix cannot hold this;
 * `Nav.labels.test.ts` now derives the required set from the modules' own
 * `register_entity()` calls and fails when a module ships without a label.
 *
 * ── …and the set is BOUNDED at the other end too (#836) ─────────────────────
 * `marketing: 'Marketing'` sat here for a module that can never install:
 * `module.json` says `"status": "planned"`, version `0.0.0`, zero migrations,
 * and `admitsModule` refuses a module that migrates nothing ("a design
 * placeholder, not an installable module"). It registers no entity, so the
 * coverage test below never required it — a label with no module behind it is
 * invisible to a check that only looks for the reverse. Removed, and
 * `Nav.labels.test.ts` now asserts the OTHER direction as well, so the next
 * planned-module label cannot accumulate here unnoticed.
 */
/**
 * A module's own page(s), listed FIRST in its generic group (#2060).
 *
 * For a module whose registered entities are its main surface but which also
 * owns one hand-built page. Joining the generic group — rather than a fixed
 * group of the same name — keeps ONE group per module (the #331 double label)
 * and keeps the entity lists; and the link exists only where the module does,
 * because the generic group is built from the installed schema.
 */
const MODULE_PAGE_LINKS: Record<string, { label: string; to: string }[]> = {
  marketing: [{ label: 'Sending domains', to: '/marketing/senders' }],
}

const MODULE_LABEL: Record<string, string> = {
  core: 'Directory', crm: 'Sales', afd: 'AFD Delivery', catalog: 'Catalog', cpq: 'CPQ',
  subscriptions: 'Recurring', compliance: 'Compliance', kyc: 'KYC & Identity', marketing: 'Marketing', time: 'Time',
  funding: 'Funding', billing: 'Billing', approvals: 'Approvals', activity: 'Activities',
  tenders: 'Bids', documents: 'Documents', contracts: 'Contracts', service: 'Service',
  commerce: 'Commerce', comms: 'Comms', portal: 'Portal', controlplane: 'Fleet',
  access: 'Identity & Access', 'access-recovery': 'Account Recovery', admission: 'Admission', education: 'Education', engagements: 'Engagements',
  operations: 'Operations', partners: 'Partners', productions: 'Productions',
  'admission-engagements': 'Attendance Reconciliation',
  // Not "Connector": the nav names what an operator DOES here, and what they do
  // is govern connector scope — propose it, approve it through the approvals
  // engine, and read its health and evidence. The signer itself lives outside
  // the database entirely (#2137).
  'approvals-connector': 'Connector Scope',
  // Field capture from the browser extension. NOT "Feedback": `afd` already
  // shows delivery feedback as "Capture feedback" one group away, and two nav
  // items reading the same word for two domains is exactly the #331 defect.
  feedback: 'Field Capture',
  // Catalog restock is a bounded catalog capability over core.notify, not a
  // dependency on the optional `comms` module. Name the human outcome, not the
  // implementation primitive (and avoid a second "Comms" beside the real one).
  'catalog-restock': 'Restock Alerts',
  // The tenders↔cpq bridge — bid pricing, not a second CPQ.
  'tenders-cpq': 'Bid Pricing',
  // The partners↔billing bridge — partner payout statements. Not "Partners"
  // (that is the channel itself, one group away) and not "Billing" (that is
  // money coming in; this is the statement money goes out against).
  'partners-billing': 'Partner Payouts',
  // The cpq↔billing bridge — what a customer COMMITTED to, the windows it is
  // trued up over, and how an early exit settles. Deliberately none of the four
  // words already in this bar: not "Contracts" (that is the legal instrument,
  // one group away), not "CPQ" (the deal that produced the terms), not
  // "Billing" (where the drafts land), and not "Recurring" (subscriptions bill
  // a quantity; this one measures against a promise).
  'cpq-billing': 'Commitments',
  'moonbase-talent': 'Talent',
  // Moonbase's own inbound onboarding: companies applying for an instance, and
  // colleagues being invited into one. NOT "Portal" (that is the customer-facing
  // surface itself, one group away) and not "Access" — nothing here grants
  // anything; a registration is an application waiting on a human.
  'moonbase-onboarding': 'Onboarding',
  // Moonbase's reusable product-instance intake and pairing workspace. Keep it
  // distinct from company onboarding above: this domain starts only after a
  // customer exists and never grants deployment access or entitlement itself.
  'product-onboarding': 'Product Onboarding',
  // The bridge where a paired candidate becomes a canonical environment and its
  // installation key rotates or recovers (#195). Operators review collisions and
  // recoveries here; "Environment Pairing" names what it governs, not the seam.
  'product-onboarding-controlplane': 'Environment Pairing',
  // Moonbase's own pursuit cockpit over the shared tenders engine (#1649).
  // "Bid Desk" and not "Bids": `tenders` already holds that word one line above,
  // and they are two lenses on ONE pursuit — a second product name for the same
  // record is the #331 defect said the other way round.
  'moonbase-bid-desk': 'Bid Desk',
  investigations: 'Context',
  // Customer-specific modules. Entitled to one deployment, but they render in
  // that deployment's sidebar like anything else and deserve a human name.
  'cocco-b2b': 'Cocco B2B', 'cocco-fefo': 'Cocco Stock', 'cocco-gifting': 'Cocco Gifting',
  'cocco-loyalty': 'Cocco Loyalty', 'cocco-quiz': 'Cocco Quiz',
  'cocco-subscriptions': 'Cocco Subscriptions',
}

/* ── ICONS ARE ON SECTIONS ONLY (#2406 navbar sheet, marked LOCKED) ─────────
 *
 * *"Icons appear on sections only. Items are text, so the hierarchy is carried
 * by position rather than repeated glyphs."* So a destination draws no glyph
 * and a SECTION draws one 15px mark beside its engraved label — and the mark
 * is also what the section reduces to when the rail collapses to its icon
 * wall, which is why every section needs one.
 *
 * Keyed by the LABEL the built-in document writes (above and in MODULE_LABEL),
 * because a navigation document carries no section icon: `NavGroup` has no
 * such field, and adding one would be a stored-document change for a
 * presentation choice. The sheet itself calls these "a first assignment [that]
 * should be reviewed per section", so they are presentation, not data — a
 * persona- or person-authored section that reuses a built-in label gets the
 * same mark, and one that does not falls back to its first destination's own
 * entity glyph, and then to a neutral one. Nothing is fabricated: a section
 * without a declared mark is drawn with the plainest one there is. */
const SECTION_ICON: Readonly<Record<string, LucideIcon>> = {
  // the sheet's own first assignment, verbatim where it names the section
  Work: Inbox, CRM: Users, Catalog: Package, CPQ: FileText, Recurring: Repeat, Billing: CreditCard,
  Delivery: Truck, Service: LifeBuoy, 'Field Capture': Camera, Contracts: FileCheck, Bids: ClipboardList,
  Commerce: ShoppingCart, Engage: Megaphone, 'Time & Funding': Clock, 'Identity & Access': KeyRound,
  Activities: Activity,
  // the rest of the built-in document and the module labels above
  Pinned: Pin, Products: Boxes, Context: ScanSearch, 'Your organization': Building2,
  Directory: BookUser, Sales: Users, Compliance: ShieldCheck, 'KYC & Identity': BadgeCheck,
  Documents: Files, Approvals: ListChecks, Comms: Mail, Portal: PanelsTopLeft, Fleet: Server,
  Admission: DoorOpen, Education: GraduationCap, Engagements: Handshake, Operations: Workflow,
  Partners: Handshake, Productions: Clapperboard, Onboarding: UserPlus, Talent: UserSearch,
  'Bid Desk': Target, 'Bid Pricing': Calculator, Commitments: Scale, 'Partner Payouts': Wallet,
  'Restock Alerts': BellRing, 'Attendance Reconciliation': CalendarCheck, Marketing: Megaphone,
  Time: Clock, Funding: PiggyBank, 'AFD Delivery': Truck,
}

/** The one mark a section carries: its declared one, its first destination's, or the plainest. */
export function SectionIcon({ label, items, className, size = 15 }: {
  label: string
  items: readonly { iconKey?: string }[]
  className?: string
  size?: number
}): ReactNode {
  const Declared = SECTION_ICON[label] ?? (label.startsWith('Cocco ') ? Store : undefined)
  if (Declared) return <Declared aria-hidden focusable="false" size={size} className={className} />
  const first = items.find((item) => item.iconKey)?.iconKey
  if (first) return <EntityIcon iconKey={first} size={size} className={className} />
  return <Layers3 aria-hidden focusable="false" size={size} className={className} />
}

/* ── #511 · the built-in bar, as a DOCUMENT ────────────────────────────────
 *
 * This is the hardcoded navigation, unchanged in content and moved out of JSX
 * into the shape `core.navigation()` returns. Nothing was removed: the fifteen
 * groups, their order, their `reg.isInstalled` gating, the fleet gate and the
 * generic `buildNav` tail are all still here. `Nav.builtin.test.ts` pins the
 * exact link set against what the JSX produced.
 *
 * It stays for two reasons, and both matter:
 *
 *  1. It is the NULL FALLBACK the cascade's migration promised. A deployment
 *     older than core 0.150 has no resolver at all, and one that has configured
 *     nothing gets NULL — which is the answer "use your own bar", not "draw
 *     nothing" (UX-03, the bare `return null` that made the whole navigation
 *     vanish on any failure).
 *
 *  2. It is what a person PERSONALISES. If the built-in bar stayed as JSX, then
 *     "reorder and hide your navigation" would only work for the few people whose
 *     persona happened to have a document — i.e. for almost nobody. A document
 *     is the only thing an editor can edit.
 *
 * Being a document does NOT make it configuration: nothing here is stored, and
 * it is recomputed from the installed modules on every render. That is exactly
 * why it can be the fallback — it follows the deployment, and a stored document
 * cannot.
 */
export function builtinNav(opts: {
  showBackstage: boolean
  isInstalled: (key: string) => boolean
  fleet: boolean
  generic: { module: string; entities: { key: string; label: string; iconKey?: string }[] }[]
}): NavDocument {
  /* `settings` defaults to the seven PLATFORM destinations and, since #1680,
     nothing overrides it. Both callers used to pass
     `settingsDestinations(useSettingModules().modules)` — the platform seven
     PLUS one entry per module that declares a setting — so the global sidebar
     grew a Settings group as long as the install list, and the settings page
     then drew the same array a second time from the same function. That is the
     defect reported against the live deployment: "the global application
     navigation and then a second tall list of every module inside the Settings
     content".

     The module tail belongs to the settings page's own section navigator,
     which is addressable, searchable and scoped to where somebody already is.
     Note that `Nav.builtin.test.ts` has always pinned exactly these seven — it
     calls `builtinNav` without a `settings` argument, so the unit test was
     asserting the intended bar while the runtime rendered a longer one. */
  const { showBackstage, isInstalled: has, fleet, generic } = opts

  // Role-area gating (#37): a customer-only user (no backstage area) gets the
  // customer nav — their organization + surfaces they own — not the internal
  // operating console.
  if (!showBackstage) {
    /* NO SETTINGS DESTINATION HERE EITHER (ROOT ruling 74071 6).

       A Settings link to /settings stood on the next line. It is not in the
       `settings` group below and it is not PLATFORM_SETTINGS, so a change that
       deleted only the group would have left every customer-only principal with
       a business-navigation Settings link, and ruling 6's "no destination
       appears in both" false for exactly the population with the least
       authority. The Studio is reached from the launcher on this host too, and
       consoleLauncherActions.ts gates that tile on the same `backstage` area
       this branch is the negative of -- so a customer-only principal is offered
       neither, which is #2328 10's permission boundary.

       The GOVERNED row still disagrees: nav.customer_member.console
       (core 2026081012) carries the same two links, it outranks this fallback
       wherever that migration has run, and moving it is a core migration this
       lane may not write. Nav.builtin.test.ts pins the disagreement by name
       rather than letting it read as an oversight. */
    return { groups: [{ label: 'Your organization', items: [
      { label: 'My Organization', to: '/my-org' },
    ] }] }
  }

  const groups: NavDocument['groups'] = []
  const add = (when: boolean, label: string, items: { label: string; to: string }[]) => {
    if (when && items.length) groups.push({ label, items })
  }

  /* ── UNIVERSAL WORK COMES FIRST (#1991, ruling 72563 §3) ──────────────────
   *
   * *"Dashboard, My work and Calendar precede the domain groups when declared
   * by the resolved navigation document."* The operative half is the last
   * clause: the renderer must not sort, so "first" is a property of the
   * DOCUMENT, and for the built-in bar this is where the document is written.
   * A personalised or persona-authored bar orders itself and nothing here
   * reaches it.
   *
   * Measured before this landed: the bar opened on CRM, and `/` — the route
   * the shell lands every session on — was in the bar NOWHERE. Home was
   * reachable only by the brand lockup, which is a link nobody reads as one.
   *
   * TWO destinations, not the three the ruling names, and the missing one is
   * deliberate. `/` renders `GovernedHome` -> the shared `WorkHome`, whose
   * feed is titled "What needs you" and whose second slot is the caller's own
   * pins: dashboard and my-work are ONE surface in this host today. A second
   * link to the same route, named differently, is not a destination — it is
   * the same page twice, and a bar that lies about how many places there are
   * is worse than one that names two. The day a distinct my-work surface
   * exists it joins this group and this comment is what says why it was not
   * here before.
   *
   * Calendar MOVED here out of its own one-link `Comms` group. A heading over
   * a single destination is chrome saying nothing, and the calendar is not a
   * domain — it is the third universal surface the ruling names. */
  add(true, 'Work', [
    { label: 'Home', to: '/' },
    ...(has('comms') ? [{ label: 'Calendar', to: '/comms' }] : []),
    /* THE ONE SURVIVOR OF THE PLATFORM GROUP (ROOT ruling 74071 6):
       "keep the Approval Inbox as a business/work destination".

       It moves here rather than staying behind as a one-link group, because a
       heading over a single destination is chrome saying nothing -- the
       argument this file already makes for the calendar three lines up.

       It belongs beside Home for the reason skill rule 23 gives: an inbox is a
       queue of decisions somebody owes, and a queue reachable only by opening a
       settings overlay is a queue nobody empties. Its POLICY -- who must sign
       off on what -- went the other way, into the Studio under Operations &
       audit; platformWorkspaces.ts carries that split and its reason.

       Unconditional, exactly as it was inside the Platform group: this change
       is a reclassification, and widening or narrowing a gate inside one would
       be a second change wearing its clothes. */
    { label: 'Approval Inbox', to: '/approvals/inbox' },
  ])
  add(has('crm'), 'CRM', [{ label: 'Pipeline', to: '/crm' }])
  add(has('catalog'), 'Catalog', [
    { label: 'Products', to: '/catalog' },
    { label: 'Price lists', to: '/catalog/pricing' }])
  add(has('cpq'), 'CPQ', [{ label: 'Quotes', to: '/cpq' }])
  add(has('subscriptions'), 'Recurring', [{ label: 'Subscriptions', to: '/subscriptions' }])
  add(has('billing'), 'Billing', [{ label: 'Invoices', to: '/billing' }])
  add(has('afd'), 'Delivery', [
    { label: 'AFD Cockpit', to: '/delivery' },
    { label: 'Engagements', to: '/delivery/projects' },
    // NOT "Capture feedback": `capture` is the field-capture module's own noun
    // (it renders as "Field Capture" below), and two nav items reading each
    // other's word for two domains is the #331 defect pointing the other way.
    // The entity label agrees — afd_feedback is "Delivery Feedback" since afd 0022.
    { label: 'Delivery feedback', to: '/delivery/feedback' }])
  // Knowledge is the GENERIC entity surface, not a page (#1744 slice 6):
  // /e/service_kb_article already routes, already resolves six registered views
  // and already renders the shared record shell. It is missing from the bar only
  // because `service` is in the `bespoke` set below, which suppresses the whole
  // module's generic tail for the sake of one bespoke workspace — so the link is
  // named here rather than by dropping the module out of that set, which would
  // also offer SLA clocks, responses and asset rows as top-level destinations.
  add(has('service'), 'Service', [
    { label: 'Cases', to: '/service' },
    { label: 'Knowledge', to: '/e/service_kb_article' },
  ])
  add(has('feedback'), MODULE_LABEL.feedback!, [
    { label: 'Triage board', to: '/feedback' },
    // The four registered list views stay one click away — a board is a second
    // way to read the same queue, not a replacement for the list.
    { label: 'All captures', to: '/e/feedback_capture' }])
  add(has('contracts'), 'Contracts', [{ label: 'Agreements', to: '/contracts' }])
  // The bid desk is Moonbase's own cockpit over the shared engine
  // (`moonbase-bid-desk`, audience: moonbase-internal). It sits in the SAME
  // group as Tenders rather than a group of its own: they are two lenses on one
  // pursuit, and a second heading would read as a second product.
  add(has('tenders'), 'Bids', [
    ...(has('moonbase-bid-desk') ? [{ label: 'Bid Desk', to: '/bid-desk' }] : []),
    { label: 'Tenders', to: '/tenders' }])
  add(has('commerce'), 'Commerce', [
    { label: 'Orders', to: '/commerce' },
    { label: 'Returns', to: '/commerce/returns' }])
  add(has('engagements'), 'Engage', [
    { label: 'Interactions', to: '/interactions' },
    { label: 'Copilot guidance', to: '/ai-guidance' }])
  add(has('investigations'), 'Context', [{ label: 'Investigations', to: '/context' }])
  add(has('time') || has('funding'), 'Time & Funding', [
    ...(has('time') ? [{ label: 'My Timesheet', to: '/time' },
                       { label: 'Timesheet Review', to: '/time/review' }] : []),
    ...(has('funding') ? [{ label: 'Budgets & Burn', to: '/funding' }] : [])])

  for (const g of generic) {
    groups.push({
      label: MODULE_LABEL[g.module] ?? g.module,
      items: [...(MODULE_PAGE_LINKS[g.module] ?? []),
              ...g.entities.map((e) => ({ label: e.label, to: `/e/${e.key}`, iconKey: e.iconKey }))],
    })
  }

  // #2284: the governed operator cockpit already existed, but only as
  // "Platform / Fleet & Estate". That labels the implementation and hides the
  // product. Products is the operating group; Platform below remains instance
  // administration. The same fleet predicate still removes the whole group on
  // a customer box, so moving the label cannot widen authority.
  add(fleet, 'Products', [
    { label: 'Elevate Cockpit', to: '/ops/estate' },
    { label: 'Releases', to: '/ops/releases' },
    { label: 'Marketplace', to: '/ops/marketplace' },
  ])

  /* THE PLATFORM GROUP AND THE SETTINGS GROUP ARE GONE.
   *
   * ROOT ruling 74071 6, verbatim: "The current Platform group is not
   * grandfathered. Classification, not its present group name, decides where
   * each destination lives" -- and "remove an empty Platform group rather than
   * preserving it as chrome". #2328 1 is the same sentence from the other end:
   * "The navigation rail contains task/business destinations only. Remove
   * Settings groups, settings pages, persona controls, account controls, pins,
   * and product-brand utilities from every generated/built-in navigation
   * definition."
   *
   * WHAT STOOD HERE, and where each of the fourteen went:
   *
   *   - thirteen (Setup, Modules, Template Studio, Approvals configuration,
   *     Template Designer, Scheduler & Relay, Environments, Flows, Rules,
   *     People & Access, Personas, My Organization, Design system) are now
   *     registered Settings Studio destinations. Their CLASSIFICATION, with a
   *     reason written beside each one, is
   *     apps/console/src/settings/platformWorkspaces.ts; the Studio that draws
   *     them is apps/console/src/settings/SettingsStudio.tsx.
   *   - one (Approval Inbox) stayed, and moved into Work above.
   *
   * The seven-destination Settings group that followed goes with the same
   * sentence. Its destinations are not deleted: /settings still routes,
   * settingsDestinations() is still the authority for what it contains, and the
   * Studio registers every one of them. What changed is the way in -- the
   * launcher, which #2328 calls "the deliberate gateway".
   *
   * NOTHING IS DELETED, AND THAT IS THE POINT. #2328 1: "Do not delete the
   * underlying settings routes or authority; only remove them from business
   * navigation." Every route named above still exists in router.tsx, every door
   * still authorizes itself, and a deep link to any of them still resolves for
   * a principal who may reach it. 8's "UI omission is not authority" is the
   * whole reason that is safe to say.
   *
   * WHAT IS NOT REMOVED, AND WHY. The fleet-gated Products group above (Elevate
   * Cockpit, Releases, Marketplace) stays. #2284 moved those OUT of Platform
   * deliberately -- "Platform below remains instance administration", Products
   * is the operating group -- so they were classified as business work a
   * milestone before this ruling, by the same test. Operating a product estate
   * is work, not configuration of this deployment.
   *
   * There are no persona controls, account controls or pins in this document to
   * remove: measured, builtinNav has never carried one. The persona and the
   * signed-in user are header controls (B2), pins are the Recent & Pinned
   * surface (#2325), and the product-brand utility is the floating launcher
   * (B3). This comment records the measurement so the next reader does not
   * re-run it looking for a deletion that was never owed.
   *
   * navigationClassification.test.ts asserts the property rather than this
   * prose: no destination appears in both the business bar and the Studio, in
   * both directions, at four deployment shapes. */

  return { groups }
}

/**
 * Which document the bar draws, given what the cascade answered.
 *
 * Pure and exported so the three cases can be proven without a renderer, a
 * router or a database — they are the whole of #511's client contract and none
 * of them is visible in a screenshot:
 *
 *   nothing configured (NULL)  → the client's own bar
 *   a persona / deployment doc → exactly as authored
 *   the caller's OWN pin       → reconciled against the client's own bar, so a
 *                                module installed after the pin still appears
 */
export function resolveBar(
  configured: NavDocument | null | undefined, builtin: NavDocument, pinned: boolean,
): NavDocument {
  // `isNavDocument`, not truthiness: the answer comes back from a jsonb RPC,
  // and "not null" is a different question from "is a bar".
  const doc = isNavDocument(configured) && configured.groups.length > 0 ? configured : null
  if (!doc) return builtin
  return visibleNav(pinned ? reconcile(doc, builtin) : doc)
}

/**
 * What "editable in place" is handed (ruling 50100 rows 1 and 2).
 *
 * The bar does not hand its rendering to an editor and it does not open one: it
 * takes a CONTROLLER and draws its own rows differently. So the editable bar is
 * the same component, the same stylesheet and the same section order as the bar
 * a moment earlier — which is what "a faithful preview of the resulting
 * navigation" means operationally. A second component drawing a second list
 * could not make that claim; it could only intend it.
 *
 * `layerLabel` is row 5, and it is required rather than optional on purpose:
 * there is no way to enter this mode without the surface saying which layer the
 * edit lands on. It appears twice — in the landmark's accessible name, so a
 * screen-reader user hears it on entering the region, and in the dock, so a
 * sighted user reads it at the moment they reach for Save.
 */
export interface NavBarEdit {
  ctl: NavEditController
  /** "only you", "everyone here" — the door's own layer, in words. */
  layerLabel: string
}

/**
 * The one renderer. Configured or built-in, editing or not, the same markup
 * draws them all.
 *
 * `footer` renders OUTSIDE the `<nav>`, and that is not a layout detail. The
 * navigation landmark is for destinations; a "reset my navigation" control is
 * not one, and putting it inside makes it something a screen-reader user tabs
 * past while looking for a page. The portal's `nav-boundary.spec.ts` counts the
 * buttons in that landmark for exactly this reason — it caught the console's
 * sibling mistake in the portal first.
 *
 * EDIT MODE is the deliberate exception to that rule and states its reason: the
 * grips, move buttons and switches are not chrome BESIDE the destinations, they
 * are controls ON each destination, and lifting them out of the landmark would
 * separate every control from the row it acts on. The landmark says so in its
 * own name ("… — editing …"), the rows stop being links for as long as it lasts,
 * and the actions that are not per-row (Add section, Save, Cancel, Reset) are
 * still outside it.
 */
export function NavBar({ doc, label, testId, footer, currentPath, edit, pinning, collapsed = false }: {
  doc: NavDocument; label: string; testId: string; footer?: React.ReactNode; currentPath?: string
  edit?: NavBarEdit
  /**
   * The rail is reduced to its wall of section marks (#2406, Organization
   * Detail). Read mode only: editing needs the column's full width, so the
   * host expands it for as long as the mode lasts.
   */
  collapsed?: boolean
  pinning?: {
    ready: boolean
    busy: boolean
    isPinned: (to: string) => boolean
    onToggle: (item: { label: string; to: string; iconKey?: string }) => void
  }
}) {
  /* One shape for both sources. `NavEditGroup` is assignable to it, so the map
     below is written once — a second loop for edit mode is how the preview and
     the bar drift apart. */
  const groups: {
    id?: string; label: string; hidden?: boolean; recovery?: true
    items: { label: string; to: string; iconKey?: string; hidden?: boolean }[]
  }[] = edit ? edit.ctl.rows : doc.groups
  const activeGroupIndex = currentPath == null ? -1 : groups.findIndex((group) =>
    group.items.some((item) => item.to.split('?')[0] === currentPath))
  const held = edit?.ctl.heldTarget
  const heldGroupIndex = held?.kind === 'group' ? held.index : held?.gi
  const disclosure = useNavigationDisclosure({
    host: 'console', groups,
    activeGroupIndex: activeGroupIndex >= 0 ? activeGroupIndex : undefined,
    heldGroupIndex: heldGroupIndex != null && heldGroupIndex >= 0 ? heldGroupIndex : undefined,
  })

  /* ── THE COLLAPSED RAIL'S FLYOUT (#2406, Organization Detail) ─────────────
   *
   * *"Collapsed, section items are NOT squeezed — they move into a seated
   * flyout, so the icons-only state loses no navigation."* One flyout at a
   * time, for the section under the pointer or holding focus.
   *
   * It is rendered INSIDE its section, straight after the section's button,
   * and positioned fixed beside the rail: so Tab walks from a section's mark
   * into that section's destinations and on to the next mark, exactly the
   * reading order of the expanded rail — a portal would have put them at the
   * end of the document. `position: fixed` is not clipped by the column's
   * scroller, which is the one thing a portal would otherwise have been for.
   *
   * Opened by hover, by focus and by a click (a click only ever OPENS: focus
   * arrives first and has already opened it, so a toggle would shut what the
   * same press opened). Closed by leaving it, by Escape (focus returns to the
   * mark), by focus leaving the section, by following a link, and by a
   * resize — a flyout left floating beside a rail that moved is a menu
   * pointing at nothing. */
  const reduced = collapsed && !edit
  const [fly, setFly] = useState<number | null>(null)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navRef = useRef<HTMLElement | null>(null)
  const flyRef = useRef<HTMLDivElement | null>(null)
  const anchorRef = useRef<HTMLElement | null>(null)
  /* Escape hands focus BACK to the section's mark — and focusing a mark opens
     its flyout. Without this the flyout closed and reopened in the same
     keystroke, so Escape did nothing a person could see. Set for exactly that
     one programmatic focus. */
  const returningFocus = useRef(false)
  const keep = () => { if (hideTimer.current) clearTimeout(hideTimer.current); hideTimer.current = null }
  const hide = () => { keep(); setFly(null) }
  /* 160ms, the sheet's own delay: long enough to cross the 8px gap between
     the rail and the flyout without it closing under the pointer. */
  const queueHide = () => { keep(); hideTimer.current = setTimeout(() => setFly(null), 160) }
  const show = (index: number, section: HTMLElement) => {
    if (!reduced) return
    keep()
    anchorRef.current = section
    setFly(index)
  }
  useEffect(() => { if (!reduced) setFly(null) }, [reduced])
  useEffect(() => { setFly(null) }, [currentPath])
  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current) }, [])
  useEffect(() => {
    if (fly === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const section = anchorRef.current
      setFly(null)
      returningFocus.current = true
      section?.querySelector<HTMLElement>('[data-testid="nav-group-disclosure"]')?.focus()
      returningFocus.current = false
    }
    const onResize = () => setFly(null)
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('resize', onResize) }
  }, [fly])
  /* Beside the rail, level with its section, and never off the bottom of the
     window: measured after it renders, because its height is its content's. */
  useLayoutEffect(() => {
    if (fly === null) return
    const panel = flyRef.current
    const section = anchorRef.current
    const nav = navRef.current
    if (!panel || !section || !nav) return
    const top = section.getBoundingClientRect().top
    panel.style.left = `${nav.getBoundingClientRect().right + 8}px`
    panel.style.top = `${Math.max(8, Math.min(top - 4, window.innerHeight - panel.offsetHeight - 8))}px`
  }, [fly])

  return (
    <>
      {/* Referenced by every grip's `aria-describedby`, so the keyboard gesture
          is stated once instead of thirty-seven times. */}
      {edit && <p id={edit.ctl.instructionsId} className={s.srOnly}>{NAV_EDIT_INSTRUCTIONS}</p>}
      {edit && (
        <div className={s.editActions}>
          <Button size="sm" variant="ghost" icon={<Plus />} data-testid="nav-add-section"
                  onClick={edit.ctl.addSection}>Add section</Button>
        </div>
      )}
      <nav data-testid={testId} data-editing={edit ? '' : undefined} ref={navRef}
           className={s.bar} data-collapsed={reduced ? '' : undefined}
           aria-label={edit ? `${label} — editing ${edit.layerLabel}` : label}>
        {groups.map((g, index) => {
          // A personalized document may contain two sections with the same
          // label. Prefer its stable id for reconciliation and collapse state;
          // the positional fallback is only for legacy/built-in documents.
          const groupKey = g.id ?? `${g.label}-${index}`
          const occurrence = groups.slice(0, index)
            .filter((row) => (row.id ?? `${row.label}-${groups.indexOf(row)}`) === groupKey).length
          const id = `nav-group-${groupKey.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-')}`
          // Edit and read mode share one disclosure model. Active and held-row
          // ancestors are forced open; other sections remain independently
          // collapsible so a large module estate stays workable.
          const open = Boolean(g.recovery) || disclosure.isOpen(index)
          const gt: NavEditTarget = { kind: 'group', index }
          const groupControlLabel = groups.some((other) =>
            other.items.some((item) => item.label === g.label))
            ? `${g.label} section`
            : g.label
          const flyId = `${id}-flyout`
          return <div key={occurrence ? `${groupKey}-${occurrence}` : groupKey}
                      className={s.group} data-open={open || undefined}
                      /* The collapsed wall keeps the vein on the section that
                         owns the current page, so place is never lost. */
                      data-current={index === activeGroupIndex ? '' : undefined}
                      onMouseEnter={reduced ? (e) => show(index, e.currentTarget) : undefined}
                      onMouseLeave={reduced ? queueHide : undefined}
                      onBlur={reduced ? (e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) hide()
                      } : undefined}
                      role={edit ? 'group' : undefined} aria-label={edit ? g.label : undefined}
                      data-hidden={edit && g.hidden ? '' : undefined}
                      data-recovery={g.recovery ? '' : undefined}
                      data-grabbed={edit?.ctl.isGrabbed(gt) ? '' : undefined}
                      data-dragging={edit?.ctl.isDragging(gt) ? '' : undefined}
                      {...(edit ? edit.ctl.dropProps(gt) : {})}
                      data-testid={`nav-group-${g.label.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            {edit ? (
              <NavigationSectionHeadRow open={open} className={s.editHead}>
                {!g.recovery && <Button size="sm" variant="ghost" icon={<GripVertical />} className={s.grip}
                        {...edit.ctl.gripProps(gt, groupControlLabel)} />}
                {edit.ctl.renaming === g.id
                  ? <Input className={s.editName} {...edit.ctl.renameProps(index)} />
                  : g.recovery
                    ? <span className={s.editLabel} id="nav-recovery-section" tabIndex={-1}>{g.label}</span>
                    : <Button size="sm" variant="ghost" className={s.editDisclosure}
                        icon={open ? <ChevronDown /> : <ChevronRight />}
                        data-testid="nav-group-disclosure"
                        aria-expanded={open} aria-controls={id}
                        onDragEnter={() => disclosure.hoverOpen(index)}
                        onDragLeave={disclosure.cancelHoverOpen}
                        onClick={() => disclosure.toggle(index)}>{g.label}</Button>}
                {!g.recovery && <span className={s.editControls}>
                  <CustomizeRowMenu
                    label={groupControlLabel}
                    hidden={g.hidden}
                    canMoveUp={index > 0} canMoveDown={index < groups.length - 1 && !groups[index + 1]?.recovery}
                    onMoveUp={() => edit.ctl.move(gt, { kind: 'group', index: index - 1 })}
                    onMoveDown={() => edit.ctl.move(gt, { kind: 'group', index: index + 1 })}
                    onToggleHidden={() => edit.ctl.setGroupHidden(index, !g.hidden)}
                    onRename={g.id && edit.ctl.renaming !== g.id
                      ? () => edit.ctl.beginRename(index) : undefined}
                    onDelete={g.id ? () => edit.ctl.deleteSection(index) : undefined} />
                </span>}
              </NavigationSectionHeadRow>
            ) : (
              /* The sheet's section label: a 15px mark on the left, the engraved
                 name, and ONE chevron on the right that rotates 90° when the
                 section opens — a rotation, not a swap of two glyphs, so the
                 mark reads as the same object changing state. Collapsed, the
                 name is not drawn, so it becomes the accessible name. The kit
                 draws it (`NavigationSectionHead`, #2406 b3-shell); this passes
                 the three states it reads. */
              <NavigationSectionHead
                      icon={<SectionIcon label={g.label} items={g.items} />}
                      label={g.label}
                      open={open}
                      collapsed={reduced}
                      current={index === activeGroupIndex}
                      aria-expanded={reduced ? fly === index : open}
                      aria-controls={reduced ? flyId : id}
                      aria-label={reduced ? g.label : undefined}
                      title={reduced ? g.label : undefined}
                      data-testid="nav-group-disclosure"
                      onFocus={reduced ? (e) => {
                        if (!returningFocus.current) show(index, e.currentTarget.parentElement!)
                      } : undefined}
                      onClick={(e) => reduced
                        ? show(index, e.currentTarget.parentElement!)
                        : disclosure.toggle(index)} />
            )}
            {reduced && fly === index && (
              <div ref={flyRef} id={flyId} className={s.flyout} data-testid="nav-flyout"
                   onMouseEnter={keep} onMouseLeave={queueHide}>
                <div className={s.flyHead} aria-hidden>
                  <SectionIcon label={g.label} items={g.items} className={s.flyIcon} size={14} />
                  {g.label}
                </div>
                <div className={s.flyBody}>
                  {g.items.map((it) => (
                    <Link key={it.to} to={it.to} className={s.flyItem} onClick={hide}
                          activeProps={{ className: s.flyActive, 'aria-current': 'page' }}>
                      {it.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <div id={id} className={s.items} hidden={!open}
                 {...(edit ? edit.ctl.listDropProps(index) : {})}>
              {g.items.map((it, ii) => {
                const it_t: NavEditTarget = { kind: 'item', gi: index, index: ii }
                if (!edit) return (
                  <div key={it.to} className={s.readRow}>
                    {/* The kit's destination on the router's link (#2624): the
                        router says which page is current, the kit draws it. */}
                    <NavigationDestination asChild>
                      <Link to={it.to} className={s.readLink} activeProps={ROUTER_STATES_CURRENT}>
                        {it.label}
                      </Link>
                    </NavigationDestination>
                    {pinning && (
                      <CustomizeRowMenu label={it.label} pinned={pinning.isPinned(it.to)}
                        onTogglePinned={pinning.ready && !pinning.busy
                          ? () => pinning.onToggle(it) : undefined} />
                    )}
                  </div>
                )
                /* NOT a Link while editing. Following one would leave the mode
                   and take every unsaved reorder with it — row 3's "unsaved
                   navigation changes must never silently disappear" said as a
                   property of the markup rather than as a guard on top of it. */
                /* The row being edited is the destination it previews, drawn by
                   the same kit primitive on an element this bar owns: no
                   current statement and no navigation while it lasts (#2624). */
                return (
                  <NavigationDestination asChild key={it.to}>
                  <div className={s.editRow}
                       data-hidden={it.hidden ? '' : undefined}
                       data-grabbed={edit.ctl.isGrabbed(it_t) ? '' : undefined}
                       data-dragging={edit.ctl.isDragging(it_t) ? '' : undefined}
                       {...edit.ctl.dropProps(it_t)}>
                    <Button size="sm" variant="ghost" icon={<GripVertical />} className={s.grip}
                            {...edit.ctl.gripProps(it_t, it.label)} />
                    <span className={s.editLabel}>{it.label}</span>
                    <span className={s.editControls}>
                      <CustomizeRowMenu label={it.label} hidden={it.hidden}
                        canMoveUp={index > 0 || ii > 0}
                        canMoveDown={ii < g.items.length - 1
                          || (index < groups.length - 1 && !groups[index + 1]?.recovery)}
                        onMoveUp={() => edit.ctl.move(it_t, ii > 0
                          ? { kind: 'item', gi: index, index: ii - 1 }
                          : { kind: 'item', gi: index - 1, index: groups[index - 1]!.items.length })}
                        onMoveDown={() => edit.ctl.move(it_t, ii < g.items.length - 1
                          ? { kind: 'item', gi: index, index: ii + 1 }
                          : { kind: 'item', gi: index + 1, index: 0 })}
                        onToggleHidden={() => edit.ctl.setItemHidden(index, ii, !it.hidden)}
                        sections={groups.flatMap((group, gi) => group.recovery ? [] : [{
                          key: group.id ?? group.label,
                          label: group.label,
                          current: gi === index,
                          onSelect: () => {
                            disclosure.open(gi)
                            edit.ctl.move(it_t, { kind: 'item', gi, index: group.items.length })
                          },
                        }])} />
                    </span>
                  </div>
                  </NavigationDestination>
                )
              })}
              {/* An invented section is a drop target with nothing in it, which
                  reads as a rendering bug rather than an invitation. */}
              {edit && g.items.length === 0 && (
                <p className={s.editEmpty} data-testid="nav-section-empty">
                  Drag links here, or use Space and the arrows.
                </p>
              )}
            </div>
          </div>
        })}
      </nav>
      {/* A reorder nobody hears is a reorder nobody made. Outside the landmark,
          because a live region is not a destination. */}
      {edit && (
        <p role="status" aria-live="polite" className={s.srOnly}
           data-testid="nav-editor-announce">{edit.ctl.said}</p>
      )}
      {/* Not drawn on the collapsed wall, whose 60px holds marks and nothing
          else; the reset is one press away on the expanded rail. */}
      {footer && !reduced && <div role="group" aria-label="Navigation settings">{footer}</div>}
    </>
  )
}

/**
 * The built-in bar for THIS deployment and THIS caller, or null while the
 * metamodel is still loading.
 *
 * Shared with the personalise dialog, which needs the same base the sidebar
 * draws: an editor working from a different list would offer to hide things the
 * bar does not have and miss things it does.
 */
export function useBuiltinNav(): NavDocument | null {
  const schema = useSchema()
  const semantics = useSemantics()
  const identities = useEntityIdentities()
  const reg = useRegistry()
  const areas = useMyAreas()
  if (!schema.data || !semantics.data) return null
  const showBackstage = !areas.data || areas.data.includes('backstage')
  // catalog + cpq + billing + subscriptions have bespoke sections; drop their generic groups.
  const bespoke = new Set(['catalog', 'cpq', 'billing', 'subscriptions', 'service', 'contracts', 'commerce', 'tenders', 'feedback', 'investigations'])
  return builtinNav({
    showBackstage,
    isInstalled: (k) => reg.isInstalled(k),
    fleet: showsFleetSurfaces(consoleProfile(), (k) => reg.isInstalled(k)),
    generic: buildNav(schema.data, semantics.data, identities.data).filter((g) => !bespoke.has(g.module)),
  })
}

export function Nav({ collapsed = false }: { collapsed?: boolean } = {}) {
  const schema = useSchema()
  const semantics = useSemantics()
  const identities = useEntityIdentities()
  const reg = useRegistry()
  const areas = useMyAreas()
  const nav = useNavigation()
  const pin = useNavigationPin()
  const setPin = usePinNavigation()
  const currentPath = useRouterState({ select: (state) => state.location.pathname })
  const navPins = usePins()
  // A bare `return null` here made the ENTIRE navigation disappear on any
  // failure — RLS denial, unexposed schema, dropped connection — with nothing
  // said and nothing to press (UX-03).
  const state = queryState([schema, semantics])
  if (state.kind !== 'ready') {
    return (
      <div className={s.loading}>
        <QueryBoundary queries={[schema, semantics]} label="the navigation">{() => null}</QueryBoundary>
      </div>
    )
  }
  // Narrowing only: queryState above already returns a non-ready kind when any
  // query settled without data, so this cannot be reached at runtime. It exists
  // so TypeScript knows `.data` is present below — NOT as the silent blank page
  // it replaced.
  if (!schema.data || !semantics.data) return null

  // Default to backstage while access loads so staff never see an empty nav flash.
  const showBackstage = !areas.data || areas.data.includes('backstage')

  // catalog + cpq + billing + subscriptions have bespoke sections; drop their generic groups.
  const bespoke = new Set(['catalog', 'cpq', 'billing', 'subscriptions', 'service', 'contracts', 'commerce', 'tenders', 'feedback', 'investigations'])
  const builtin = builtinNav({
    showBackstage,
    isInstalled: (k) => reg.isInstalled(k),
    fleet: showsFleetSurfaces(consoleProfile(), (k) => reg.isInstalled(k)),
    generic: buildNav(schema.data, semantics.data, identities.data).filter((g) => !bespoke.has(g.module)),
  })

  /* ── #511: the navigation is CONFIGURATION, resolved by the cascade ───────
   *
   * `core.navigation('console')` resolves user pin → persona default → a
   * deployment-wide default, and returns `{ groups: [...] }`. When it answers,
   * it wins. Configuring the bar is then a row, not a release.
   *
   * The precedence is NOT re-implemented here. If the console decided which of
   * the three layers applied, it would eventually disagree with the database
   * about which nav somebody actually has, and only one of those is the one that
   * was configured.
   *
   * NULL means nothing is configured — the built-in bar above. On a FRESH
   * install that branch was unreachable until core 0.179: 2026080406 seeds a
   * `nav.default` of two groups and three links with `persona is null`, which
   * matches every caller, so a newly-migrated box drew three links in place of
   * thirty-seven. The layer survives, the seed does not. (Read as a property of
   * the tree, not of any running deployment — an existing box may have had that
   * row replaced by hand, and 0.179's delete is conditional so that it keeps it.)
   */
  // A PIN reconciles against the built-in bar; a persona or deployment default
  // is rendered exactly as authored (`navigationDocument.ts` states why).
  // A pin is a DOCUMENT, not merely a non-null answer.
  const pinned = isNavDocument(pin.data)
  const resolved = resolveBar(nav.data, builtin, pinned)
  /* Pins are navigation shortcuts, not dashboard content and not a second
     navigation document. Keep the governed `pins` store as the only owner and
     project its current rows into one fixed, first section. The authored bar is
     left intact underneath so pinning never edits a deployment/persona IA. */
  const doc = withPinnedNavigation(resolved, navPins.items)
  const configured = resolved !== builtin

  return (
    <NavBar
      doc={doc}
      testId={configured ? 'nav-configured' : showBackstage ? 'nav-builtin' : 'nav-customer'}
      label={showBackstage ? 'Platform' : 'Customer'}
      currentPath={currentPath}
      collapsed={collapsed}
      pinning={{
        ready: navPins.ready,
        busy: navPins.busy,
        isPinned: navPins.isPinned,
        onToggle: (item) => navPins.toggle({
          kind: 'list', scope: 'navigation', type: 'Page', name: item.label, to: item.to,
        }),
      }}
      footer={pinned && (
        /* Every personalised bar SAYS it is personalised and offers one click
           back. Without this the pin is a thing that happened to you once: the
           resolver returns a document with no provenance, so a reordered
           sidebar and an administrator's decision look identical. */
        <div className={s.group} data-testid="nav-customised">
          <NavigationSectionHeadRow><em>· customised</em></NavigationSectionHeadRow>
          {/* The kit's ghost Button as drawn — it is an action, not a
              destination, so it no longer borrows the destination's paint
              (which left this sheet for the kit in #2624). */}
          <Button size="sm" variant="ghost" icon={<RotateCcw />}
                  data-testid="nav-reset" disabled={setPin.isPending}
                  onClick={() => setPin.mutate(null)}>
            Reset navigation
          </Button>
        </div>
      )}
    />
  )
}
